import React from 'react';
import { DNABox, DNAButton, DNAButtonProps, DNAText, Iffy } from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover'
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { StyleSheet } from 'react-native';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { useContentPreviewModal } from './State/ContentPreviewModalStateProvider';
import { useMeetingPresentedMeta } from './State/MeetingPresentedMetaProvider';
import { Sentiment } from '@alucio/aws-beacon-amplify/src/models';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useIsMeetingLocked } from '../Meeting/AddMeetingProvider';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import {
  getVisiblePresentationPageCounter,
  shouldHidePresentationPageCounter,
} from 'src/utils/hiddenSlides';

const styles = StyleSheet.create({
  mainContainer: {
    padding: 8,
    backgroundColor: colors['color-gray-900'],
  },
  tabletMainContainer: {
    paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
    minHeight: 'calc(56px + env(safe-area-inset-bottom, 0px))',
    zIndex: 50,
    overflow: 'visible',
  },
  tooltipText: {
    color: colors['color-text-white'],
  },
});

const defaultButtonProps: DNAButtonProps = {
  appearance: 'outline',
  status: 'subtle',
  colorMode: 'dark',
}

interface ActionBarProps {
  hideSearch?: boolean,
}

const ActionBar: React.FC<ActionBarProps> = ({ hideSearch }) => {
  const { activePresentation, nextStepPage, prevStepPage } = useContent();
  const { slideRollVisible, setSlideRollVisible, textSearchVisible, setTextSearchVisible } = useContentPreviewModal()
  const { meetingORM, currentPresentedMeta, setSentiment, setFollowUp, setIsPresented } = useMeetingPresentedMeta();
  const featureFlags = useFeatureFlags(
    'enableMeetingReactions',
    'enableMeetingFollowUp',
    'enableMeetingEyeball',
    'BEAC_7666_enable_hidden_slides',
  );
  const isLocked = useIsMeetingLocked(meetingORM);
  const { deviceMode } = useAppSettings();

  if (!activePresentation) return null

  const { presentable, currentPresentablePage } = activePresentation;
  const currentPageNumber = currentPresentablePage.presentationPageNumber;
  const pageCounter = featureFlags.BEAC_7666_enable_hidden_slides
    ? getVisiblePresentationPageCounter({
      pages: presentable.presentablePages,
      currentPresentationPageNumber: currentPageNumber,
    })
    : {
      currentPageNumber,
      totalPages: presentable.numberOfPages,
    };
  const hidePageCounter = shouldHidePresentationPageCounter({
    featureEnabled: featureFlags.BEAC_7666_enable_hidden_slides,
    pages: presentable.presentablePages,
    currentPresentationPageNumber: currentPageNumber,
  });
  return (
    <DNABox
      testID="content-navigation-container"
      spacing="sm"
      style={[styles.mainContainer, deviceMode === DeviceMode.tablet && styles.tabletMainContainer]}
      alignX="center"
      alignY="center"
    >
      {/* Previous slide button */}
      <DNAButton
        {...defaultButtonProps}
        testID="action-bar-previous-slide-btn"
        iconLeft="caret-left"
        onPress={prevStepPage}
      />

      <Iffy is={!hidePageCounter}>
        <DNAText testID="slide-of-number" colorMode="dark">
          {`${pageCounter.currentPageNumber} of ${pageCounter.totalPages}`}
        </DNAText>
      </Iffy>
      {/* Next slide button */}
      <DNAButton
        {...defaultButtonProps}
        testID="action-bar-next-slide-btn"
        iconRight="caret-right"
        onPress={nextStepPage}
      />
      {/* slide roll button */}
      <DNAPopover disablePopover={['tabletPWA']}>
        <DNAPopover.Anchor>
          <DNAButton
            {...defaultButtonProps}
            testID="action-bar-slide-roll-btn"
            status={slideRollVisible ? 'primary' : 'subtle'}
            onPress={() => {
              setSlideRollVisible(p => !p)
              setTextSearchVisible(false)
            }}
          >
            Slides
          </DNAButton>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText style={styles.tooltipText} numberOfLines={1}>Toggle slides</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
      {/* search  button */}
      <Iffy is={!hideSearch}>
        <DNAPopover disablePopover={['tabletPWA']}>
          <DNAPopover.Anchor>
            <DNAButton
              {...defaultButtonProps}
              testID="magnify"
              status={textSearchVisible ? 'primary' : 'subtle'}
              iconLeft="magnifying-glass"
              onPress={() => {
                setTextSearchVisible(p => !p)
                setSlideRollVisible(false)
              }}
            />
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText
              style={styles.tooltipText}
              numberOfLines={1}
            >
              Search
            </DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      </Iffy>
      <Iffy is={meetingORM}>
        <Iffy is={featureFlags.enableMeetingReactions && !isLocked}>
          <DNABox spacing="sm">
            {/* Positive Sentiment Button */}
            <DNAPopover disablePopover={['tabletPWA']}>
              <DNAPopover.Anchor>
                <DNAButton
                  {...defaultButtonProps}
                  status={currentPresentedMeta?.sentiment === Sentiment.POSITIVE ? 'success' : 'subtle'}
                  iconLeft="caret-up"
                  onPress={() => setSentiment(Sentiment.POSITIVE)}
                />
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText colorMode="dark">Positive reaction</DNAText>
              </DNAPopover.Content>
            </DNAPopover>
            {/* Negative Sentiment Button */}
            <DNAPopover disablePopover={['tabletPWA']}>
              <DNAPopover.Anchor>
                <DNAButton
                  {...defaultButtonProps}
                  status={currentPresentedMeta?.sentiment === Sentiment.NEGATIVE ? 'warning' : 'subtle'}
                  iconLeft="caret-down"
                  onPress={() => setSentiment(Sentiment.NEGATIVE)}
                />
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText colorMode="dark">Negative reaction</DNAText>
              </DNAPopover.Content>
            </DNAPopover>
          </DNABox>
        </Iffy>
        {/* Follow-up Button */}
        <Iffy is={featureFlags.enableMeetingFollowUp && !isLocked}>
          <DNAPopover
            placement="top"
            type="tooltip"
            offset={30}
            disablePopover={['tabletPWA']}
          >
            <DNAPopover.Anchor>
              <DNAButton
                {...defaultButtonProps}
                status={currentPresentedMeta?.followUp ? 'primary' : 'subtle'}
                iconLeft={currentPresentedMeta?.followUp ? 'flag-fill' : 'flag'}
                onPress={setFollowUp}
              />
            </DNAPopover.Anchor>
            <DNAPopover.Content>
              <DNAText colorMode="dark">Flag for follow-up</DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        </Iffy>
        {/* Presented Button */}
        <Iffy is={featureFlags.enableMeetingEyeball && !isLocked}>
          <DNAPopover
            placement="top"
            type="tooltip"
            offset={30}
            disablePopover={['tabletPWA']}
          >
            <DNAPopover.Anchor>
              <DNAButton
                {...defaultButtonProps}
                iconLeft={currentPresentedMeta?.presented ? 'eye' : 'eye-slash'}
                onPress={setIsPresented}
              />
            </DNAPopover.Anchor>
            <DNAPopover.Content>
              <DNAText colorMode="dark">
                {currentPresentedMeta?.presented ? 'Mark as unpresented slide' : 'Mark as presented slide'}
              </DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        </Iffy>
      </Iffy>
    </DNABox >
  )
}

export default ActionBar;
